import type { ReactNode } from 'react';
import { Dialog } from './Dialog';
import { Icon, type IconName } from './Icon';

/**
 * A "TOBB" LAP - telefonon az ölelés másodlagos funkciói (alulról jövő lap).
 *
 * A hívás közbeni funkciók száma megnőtt (szavazás, stopper, kiscsoportok, vendég,
 * jegyzetvászon, átirat…), és egy keskeny képernyőn nem fér ki mind a fejlécbe és a
 * vezérlősávba. A sávban csak az marad, amit hívás közben folyton nyomunk
 * (mikrofon, kamera, megosztás, chat, kilépés); minden más ide kerül, nagy,
 * hüvelykujjal eltalálható csempékre, a gyakoriak elöl.
 */

export interface MoreTile {
  key: string;
  icon: IconName;
  label: string;
  /** Be van-e kapcsolva (kiemelve látszik). */
  active?: boolean;
  /** Pici jelzőpont a sarkán (pl. nyitott szavazás). */
  badge?: boolean;
  onSelect: () => void;
  /** A lap NYITVA marad a megérintés után (pl. ha a csempe maga nyit alpanelt a lapon). */
  keepOpen?: boolean;
}

export function HuddleMoreSheet({
  open,
  onOpenChange,
  reactions,
  onReact,
  tiles,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reactions: readonly string[];
  onReact: (emoji: string) => void;
  tiles: MoreTile[];
  /** További szakaszok a csempék alatt (megosztások, stopper, hang). */
  children?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Több">
      <div className="space-y-4 pt-3">
        {/* Reakciók: egy érintés, a lap bezárul. */}
        <div className="flex justify-between gap-1" role="group" aria-label="Reakciók">
          {reactions.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onReact(emoji);
                onOpenChange(false);
              }}
              className="grid size-10 place-items-center rounded-control text-2xl transition-transform active:scale-90"
            >
              {emoji}
            </button>
          ))}
        </div>

        <ul className="grid grid-cols-3 gap-2">
          {tiles.map((tile) => (
            <li key={tile.key}>
              <button
                type="button"
                aria-pressed={tile.active}
                onClick={() => {
                  tile.onSelect();
                  if (!tile.keepOpen) onOpenChange(false);
                }}
                className={[
                  'relative flex h-full min-h-[4.5rem] w-full flex-col items-center justify-center gap-1',
                  'rounded-card border px-1.5 py-2 text-center text-2xs font-medium leading-tight',
                  'transition-colors active:scale-95',
                  tile.active
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft bg-surface text-ink-muted hover:border-border-strong hover:text-ink',
                ].join(' ')}
              >
                <span aria-hidden className="text-xl">
                  <Icon name={tile.icon} />
                </span>
                {tile.label}
                {tile.badge && (
                  <span
                    role="img"
                    aria-label="van új"
                    className="absolute right-1.5 top-1.5 size-2.5 rounded-full border-2 border-surface bg-[var(--danger)]"
                  />
                )}
              </button>
            </li>
          ))}
        </ul>

        {children}
      </div>
    </Dialog>
  );
}

/** A lap szakasza: kis felirat és a tartalma. */
export function MoreSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border-soft pt-3">
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
        {title}
      </h3>
      {children}
    </section>
  );
}
